import { observer } from "mobx-react-lite";

import { DECISIONS_API } from "../constants";
import { useParameters } from "../store";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";

export const APISelector = observer(() => {
  const store = useParameters();
  const isOpenAIProvider =
    store.provider?.name === "openai" || store.provider?.name === "openai.azure";
  const isCustomOpenAIProvider =
    store.provider?.workspace_id !== undefined &&
    store.provider?.workspace_id !== null &&
    store.provider?.client === "openai";
  const isOpenRouterProvider = store.provider?.name === "openrouter";
  const shouldShowAPISelector = isOpenAIProvider || isCustomOpenAIProvider || isOpenRouterProvider;

  if (!shouldShowAPISelector) {
    return null;
  }

  const OPENROUTER_API_LABELS: Record<string, string> = {
    chat: "Chat",
    images: "Images",
    video: "Video Generation",
    speech: "Speech",
    [DECISIONS_API]: "Decisions",
  };
  const OPENROUTER_API_ORDER = ["chat", "images", "video", "speech", DECISIONS_API];

  const openRouterApiOptions = () => {
    const supported = store.provider?.supported_apis ?? [];
    // Fall back to the full list if supported_apis is not loaded yet.
    const ordered =
      supported.length > 0
        ? [
            ...OPENROUTER_API_ORDER.filter(key => supported.includes(key)),
            ...supported.filter(key => !OPENROUTER_API_ORDER.includes(key)),
          ]
        : OPENROUTER_API_ORDER;
    return ordered.map(key => ({ key, label: OPENROUTER_API_LABELS[key] ?? key }));
  };

  const apiOptions = isOpenRouterProvider
    ? openRouterApiOptions()
    : [
        { key: "chat-completions", label: "Chat Completions API" },
        { key: "responses", label: "Responses API" },
        { key: "images", label: "Images API" },
        // The Decisions API is OpenAI direct only.
        ...(store.provider?.name === "openai"
          ? [{ key: DECISIONS_API, label: "Decisions API" }]
          : []),
      ];

  const selectedOption = apiOptions.find(option => option.key === store.selectedAPI);

  const onValueChange = (value: string) => {
    store.updateSelectedAPI(value);
  };

  return (
    <div className="flex-0 mb-4 md:mb-0">
      <div className="mb-4">
        <Label htmlFor="api-select" className="mb-2 block text-sm font-medium text-gray-700">
          API
        </Label>
        <Select value={store.selectedAPI || ""} onValueChange={onValueChange}>
          <SelectTrigger className="w-full bg-white">
            <SelectValue placeholder="Select API...">
              {selectedOption && (
                <OverflowDisplayTooltip content={selectedOption.label}>
                  {selectedOption.label}
                </OverflowDisplayTooltip>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {apiOptions.map(option => (
              <SelectItem key={option.key} value={option.key}>
                <OverflowDisplayTooltip content={option.label}>
                  {option.label}
                </OverflowDisplayTooltip>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
});
